import { SERVER_URL } from "../../../constants";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

// Backend gender casing is inconsistent (students "Male"/"Female", staff
// "MALE"/"FEMALE"). Normalise to Title Case so the gender filter doesn't list
// the same gender twice and room validation compares like with like.
function normaliseGender(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

export async function fetchResidentsShared({ date, token, campus }) {
    // Single unified resident feed (students + adults + staff in one shape).
    // Scoped to the selected campus server-side; the client-side campus filter
    // then only ever sees that campus's residents. (No campus yet -> fetch all;
    // the board shows nothing until a campus is chosen anyway.)
    const query = campus ? `?campus=${encodeURIComponent(campus)}` : "";
    const data = await fetch(
        `${SERVER_URL}api/residentsbynight/${date}${query}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    ).then((res) => (res.status === 204 ? [] : res.json()));

    const list = Array.isArray(data) ? data : [];

    const normalised = list.map((r) => {
        const personType = String(r.personType || "").toUpperCase();
        const isStudent = personType === "STUDENT";
        const type = isStudent ? "student" : "adult";
        const rawId = r.personId;

        return {
            id: `${type}-${rawId}`,
            rawId,
            type,
            personType: personType || (isStudent ? "STUDENT" : "ADULT"),
            stayId: r.stayId ?? null,
            name: r.name || `${isStudent ? "Student" : "Adult"} ${rawId}`,
            surname: r.surname || "",
            gender: normaliseGender(r.gender),
            // Lean payload omits age for non-students; keep the old 0 default
            // for adults so sorting/age filters behave as before.
            age: r.age ?? (isStudent ? null : 0),
            campus: r.campus || "",
            // Operational fields not in the lean payload today — mapped
            // optimistically so they light back up with no FE change if the
            // backend re-adds them. role falls back to the person type.
            roomRequirements: r.roomRequirements ?? null,
            role: isStudent
                ? null
                : r.role ?? (personType === "STAFF" ? "Staff" : "Adult"),
            nationality: String(r.nationality ?? "").trim(),
            // Booking group the person belongs to (shown on the board card).
            // Mapped optimistically; needs the backend to add it to the
            // residentsbynight payload (see docs ticket).
            bookingGroupName: r.bookingGroupName ?? null,
            // Student "do not contact by mobile" flag — surfaced on the fire
            // list. Mapped optimistically; needs the backend to add it to the
            // residentsbynight payload (see docs ticket).
            mobilePhoneOptOut: Boolean(r.mobilePhoneOptOut),
            stay_start: r.stayStart,
            stay_end: r.stayEnd,
            // Bedroom allocations overlapping this stay, across all campuses;
            // powers the precise mid-stay placement/removal warnings.
            bedroomAllocations: r.bedroomAllocations,
        };
    });

    const seen = new Set();
    const deduped = normalised.filter((res) => {
        if (seen.has(res.id)) return false;
        seen.add(res.id);
        return true;
    });

    const campusValues = Array.from(
        new Set(deduped.map((p) => p.campus).filter(Boolean))
    );
    const genderValues = Array.from(
        new Set(deduped.map((p) => p.gender).filter(Boolean))
    );
    const ageValues = Array.from(
        new Set(
            deduped
                .filter((p) => p.type === "student")
                .map((p) => p.age)
                .filter((age) => Number.isInteger(age))
        )
    );
    const nationalityValues = Array.from(
        new Set(deduped.map((p) => p.nationality).filter(Boolean))
    );

    return {
        residents: deduped,
        campuses: campusValues,
        genders: genderValues,
        ages: ageValues,
        nationalities: nationalityValues,
    };
}

export async function fetchGroupsShared({
    token,
    date,
    campus,
    residents,
}) {
    const response = await fetch(
        `${SERVER_URL}api/bedrooms/board?campus=${encodeURIComponent(campus)}&date=${date}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );

    if (!response.ok) throw new Error("Failed to fetch bedroom board data");

    const boardRooms = await response.json();

    const groupsWithResidents = boardRooms.map((room) => {
        const matchedResidents = (room.residents || [])
            .map((assignedResident) => {
                const match = residents.find(
                    (r) =>
                        r.rawId === assignedResident.residentId &&
                        r.type === assignedResident.residentType
                );

                if (match) {
                    return match;
                }

                return {
                    id: `${assignedResident.residentType}-${assignedResident.residentId}`,
                    rawId: assignedResident.residentId,
                    type: assignedResident.residentType,
                    name: assignedResident.name || `${assignedResident.residentType} ${assignedResident.residentId}`,
                    surname: "",
                    gender: assignedResident.gender || "",
                    age: assignedResident.residentType === "student" ? null : 0,
                    campus: room.campus || "",
                    roomRequirements: null,
                    role: assignedResident.residentType === "adult" ? "Assigned" : null,
                    stay_start: assignedResident.startDate || null,
                    stay_end: assignedResident.endDate || null,
                    nationality: "",
                };
            })
            .filter(Boolean);

        return {
            id: room.bedroomId,
            groupName: room.bedroomName,
            campus: room.campus,
            capacity: room.capacity,
            notes: room.notes,
            house: room.house,
            studentIds: matchedResidents
                .filter((r) => r.type === "student")
                .map((r) => r.rawId),
            adultIds: matchedResidents
                .filter((r) => r.type === "adult")
                .map((r) => r.rawId),
            residents: matchedResidents,
        };
    });

    return groupsWithResidents;
}

export function createUpdateRoomResidents({
    displayState,
    databaseState,
    showToast,
    filteredResidents,
    selectedDate,
    selectedCampus,
    setChanges,
    setDatabaseState,
    setDisplayState,
    // Board ids ("student-123"/"adult-45") the user chose to allocate for their
    // WHOLE stay — their placement starts at stay_start instead of the viewed
    // date. Set on a mid-stay move via the whole-stay offer dialog.
    wholeStayResidentIds,
    // Board ids -> a clamped end date (YYYY-MM-DD). Set when the operator answers
    // an over-capacity rejection with "assign the room for as long as it's free":
    // the add's end date is truncated to the night before the room fills, instead
    // of running to stay_end. Read live (mutated object) so a re-save after the
    // clamp picks it up without rebuilding this closure.
    clampedEndDates,
}) {
    return async () => {
        const token = sessionStorage.getItem("bearer");
        const formattedDate = selectedDate.format("YYYY-MM-DD");
        const wholeStay = wholeStayResidentIds || new Set();
        const clamped = clampedEndDates || {};
        const startFor = (type, id, person) =>
            wholeStay.has(`${type}-${id}`) && person?.stay_start
                ? person.stay_start
                : formattedDate;
        // Clamp override wins over the resident's stay_end for an add.
        const endFor = (type, id, person) =>
            clamped[`${type}-${id}`] ?? person?.stay_end ?? null;
        const changes = [];

        displayState.rooms.forEach((room) => {
            const databaseRoom = databaseState.rooms.find((r) => r.id === room.id);

            // Diff against the room's MATCHED residents (people actually
            // resident that night), not the raw board id lists — otherwise a
            // board id that isn't in this night's resident list looks like a
            // removal and silently un-allocates that person.
            const databaseStudentIds = (databaseRoom?.residents || [])
              .filter((p) => p.type === "student")
              .map((p) => p.rawId);
            const databaseAdultIds = (databaseRoom?.residents || [])
              .filter((p) => p.type === "adult")
              .map((p) => p.rawId);

            const currentStudentIds = room.residents
                .filter((p) => p.type === "student")
                .map((p) => p.rawId);

            const currentAdultIds = room.residents
                .filter((p) => p.type === "adult")
                .map((p) => p.rawId);

            const studentsToAdd = currentStudentIds.filter(
                (id) => !databaseStudentIds.includes(id)
            );
            const studentsToRemove = databaseStudentIds.filter(
                (id) => !currentStudentIds.includes(id)
            );

            const adultsToAdd = currentAdultIds.filter(
                (id) => !databaseAdultIds.includes(id)
            );
            const adultsToRemove = databaseAdultIds.filter(
                (id) => !currentAdultIds.includes(id)
            );

            studentsToAdd.forEach((studentId) => {
                const student = filteredResidents.find(
                    (r) => r.type === "student" && r.rawId === studentId
                );

                const fromRoom = databaseState.rooms.find((dbRoom) =>
                    (dbRoom.residents || []).some(
                        (p) => p.type === "student" && p.rawId === studentId
                    )
                );

                changes.push({
                    residentId: studentId,
                    residentType: "student",
                    fromBedroomId: fromRoom?.id ?? null,
                    toBedroomId: room.id,
                    startDate: startFor("student", studentId, student),
                    endDate: endFor("student", studentId, student),
                });
            });

            studentsToRemove.forEach((studentId) => {
                // A resident who still appears in SOME room on the board
                // hasn't been removed — they've been MOVED. The matching add
                // (which carries fromBedroomId) moves them atomically, so a
                // separate removal here is not only redundant, it's
                // destructive: depending on room render order it fires an
                // open-ended "remove from this date onward" that wipes the
                // rest of their stay out of the old room. Skip it.
                const movedElsewhere = displayState.rooms.some((r) =>
                    (r.residents || []).some(
                        (p) => p.type === "student" && p.rawId === studentId
                    )
                );
                if (movedElsewhere) return;

                // Genuine removal (dragged to Unassigned). Bound the removal
                // to the resident's stay end so it releases this room from the
                // viewed date through to their departure — never open-ended,
                // which could also bleed into a later, separate stay.
                const removed = (databaseRoom?.residents || []).find(
                    (p) => p.type === "student" && p.rawId === studentId
                );
                changes.push({
                    residentId: studentId,
                    residentType: "student",
                    fromBedroomId: room.id,
                    toBedroomId: null,
                    startDate: formattedDate,
                    endDate: removed?.stay_end ?? null,
                });
            });

            adultsToAdd.forEach((adultId) => {
                const adult = filteredResidents.find(
                    (r) => r.type === "adult" && r.rawId === adultId
                );

                const fromRoom = databaseState.rooms.find((dbRoom) =>
                    (dbRoom.residents || []).some(
                        (p) => p.type === "adult" && p.rawId === adultId
                    )
                );

                changes.push({
                    residentId: adultId,
                    residentType: "adult",
                    fromBedroomId: fromRoom?.id ?? null,
                    toBedroomId: room.id,
                    startDate: startFor("adult", adultId, adult),
                    endDate: endFor("adult", adultId, adult),
                });
            });

            adultsToRemove.forEach((adultId) => {
                // See the student removal note above — a resident still
                // present in another room was moved, not removed; the add
                // handles it. Only emit a removal for a genuine unassign, and
                // bound it to the stay end rather than leaving it open-ended.
                const movedElsewhere = displayState.rooms.some((r) =>
                    (r.residents || []).some(
                        (p) => p.type === "adult" && p.rawId === adultId
                    )
                );
                if (movedElsewhere) return;

                const removed = (databaseRoom?.residents || []).find(
                    (p) => p.type === "adult" && p.rawId === adultId
                );
                changes.push({
                    residentId: adultId,
                    residentType: "adult",
                    fromBedroomId: room.id,
                    toBedroomId: null,
                    startDate: formattedDate,
                    endDate: removed?.stay_end ?? null,
                });
            });
        });

        if (changes.length === 0) {
            showToast("No room assignment changes to save.", "info");
            return;
        }

        try {
            const response = await fetch(`${SERVER_URL}api/bedrooms/board`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    campus: selectedCampus,
                    date: formattedDate,
                    changes,
                }),
            });

            if (!response.ok) {
                let body = null;
                try {
                    body = await response.json();
                } catch (_) {
                    // non-JSON error body
                }
                // Over-capacity rejection: hand the structured payload up so the
                // hook can offer "assign as long as it's free / revert" instead
                // of a dead-end toast. Marked so the catch below doesn't also
                // fire the generic error toast.
                if (
                    response.status === 409 &&
                    body &&
                    (body.code === "CAPACITY_CONFLICT" || body.firstUnavailableDate)
                ) {
                    const err = new Error(body.message || "Room is over capacity.");
                    err.code = "CAPACITY_CONFLICT";
                    err.conflict = body;
                    throw err;
                }
                // Stale save: a change's fromBedroomId no longer matches where
                // the server has that person (someone else moved them since the
                // board was loaded). The hook reloads and drops the pending
                // edits — retrying them would replay the same conflict.
                if (response.status === 409 && body && body.code === "STALE_BOARD") {
                    const err = new Error(body.message || "The board changed since you loaded it. Reload.");
                    err.code = "STALE_BOARD";
                    err.stale = body;
                    throw err;
                }
                throw new Error(
                    (body && body.message) || "Failed to save bedroom board"
                );
            }

            const updatedBoard = await response.json();

            const refreshedRooms = updatedBoard.map((room) => {
                const matchedResidents = (room.residents || [])
                    .map((assignedResident) => {
                        const match = filteredResidents.find(
                            (r) =>
                                r.rawId === assignedResident.residentId &&
                                r.type === assignedResident.residentType
                        );

                        if (match) {
                            return match;
                        }

                        return {
                            id: `${assignedResident.residentType}-${assignedResident.residentId}`,
                            rawId: assignedResident.residentId,
                            type: assignedResident.residentType,
                            name: assignedResident.name || `${assignedResident.residentType} ${assignedResident.residentId}`,
                            surname: "",
                            gender: assignedResident.gender || "",
                            age: assignedResident.residentType === "student" ? null : 0,
                            campus: room.campus || "",
                            roomRequirements: null,
                            role: assignedResident.residentType === "adult" ? "Assigned" : null,
                            stay_start: assignedResident.startDate || null,
                            stay_end: assignedResident.endDate || null,
                            nationality: "",
                        };
                    })
                    .filter(Boolean);

                return {
                    id: room.bedroomId,
                    groupName: room.bedroomName,
                    bedroomName: room.bedroomName,
                    roomName: room.bedroomName,
                    campus: room.campus,
                    capacity: room.capacity,
                    notes: room.notes,
                    house: room.house,
                    ensuite: room.ensuite,
                    studentIds: matchedResidents
                        .filter((r) => r.type === "student")
                        .map((r) => r.rawId),
                    adultIds: matchedResidents
                        .filter((r) => r.type === "adult")
                        .map((r) => r.rawId),
                    residents: matchedResidents,
                };
            });

            setDatabaseState((prev) => ({
                ...prev,
                rooms: refreshedRooms,
            }));

            setDisplayState((prev) => ({
                ...prev,
                rooms: refreshedRooms,
            }));

            setChanges([]);
            showToast("Room assignments saved successfully!", "success");
        } catch (err) {
            console.error("Error with updating residents:", err);
            // Capacity conflict (dialog) and stale board (reload) are handled by
            // the hook, which shows their own message.
            if (err.code === "CAPACITY_CONFLICT" || err.code === "STALE_BOARD") throw err;
            showToast(err.message || "Error saving room assignments", "error");
            throw err;
        }
    };
}

export function generateFireListCSV(displayState, formattedDate, showToast) {
    try {
        const header = ["House", "Room", "Name", "Type", "Gender", "Nationality", "Age", "Mobile Opt-Out"];
        const rows = [];

        displayState.rooms.forEach((room) => {
            room.residents.forEach((person) => {
                rows.push([
                    room.house || "",
                    room.groupName || room.bedroomName || room.roomName || "",
                    `${person.name} ${person.surname}`.trim(),
                    person.type === "student" ? "Student" : "Adult",
                    person.gender || "",
                    person.nationality || "",
                    person.type === "student" ? person.age : "",
                    person.type === "student"
                        ? person.mobilePhoneOptOut
                            ? "Yes"
                            : "No"
                        : "",
                ]);
            });
        });

        if (rows.length === 0) {
            showToast("No residents found for this date.", "warning");
            return;
        }

        const csvContent = [header, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        saveAs(blob, `firelist_${formattedDate}.csv`);

        showToast("Fire list downloaded successfully", "success");
    } catch (err) {
        console.error("Fire list export failed:", err);
        showToast("Error generating fire list", "error");
    }
}

export function generateRoomListPDF(
    displayState,
    formattedDate,
    selectedCampus,
    showToast
) {
    try {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(`Room List - ${selectedCampus}`, 14, 20);

        doc.setFontSize(10);
        doc.text(`Date: ${formattedDate}`, 14, 26);

        const grouped = displayState.rooms
            .filter((room) => room.groupName && room.residents?.length > 0)
            .map((room) => ({
                house: room.house || "",
                roomName: room.groupName || room.bedroomName || room.roomName || "",
                residents: room.residents || [],
            }));

        let y = 32;

        grouped.forEach((room, index) => {
            if (index > 0) y += 10;

            const hasAdults = room.residents.some((r) => r.type === "adult");

            // Room header
            autoTable(doc, {
                startY: y,
                head: [[`House: ${room.house}    Room: ${room.roomName}`]],
                theme: "plain",
                styles: { fontStyle: "bold" },
            });

            // Residents table
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 2,
                head: [[
                    "Name",
                    "Surname",
                    "",
                    "",
                    hasAdults ? "" : "Age",
                    "Mobile Opt-Out",
                ]],
                body: room.residents.map((r) => [
                    r.name,
                    r.surname,
                    r.type === "student" ? "Student" : "Adult",
                    r.gender,
                    hasAdults ? "" : (r.type === "student" ? r.age : ""),
                    r.type === "student" ? (r.mobilePhoneOptOut ? "Yes" : "No") : "",
                ]),
                theme: "grid",
                headStyles: {
                    fillColor: hasAdults
                        ? [0, 120, 0] // dark green (adult rooms)
                        : [170, 220, 170],   // light green (student rooms)
                    textColor: 255,
                },
                margin: { top: 10 },
            });

            y = doc.lastAutoTable.finalY;
        });

        if (grouped.length === 0) {
            showToast("No residents assigned to rooms.", "warning");
            return;
        }

        doc.save(`bedrooms_${formattedDate}_${selectedCampus}.pdf`);
        showToast("PDF downloaded successfully");
    } catch (err) {
        console.error("PDF export failed:", err);
        showToast("PDF export failed", "error");
    }
}